import type { ReactNode } from 'react';
import { Heading } from 'react-aria-components';

import { cx } from '../../../utils/cx';
import { CirclePattern } from '../../foundations/decorative-patterns';

export const MODAL_HEADER_LAYOUTS = ['vertical', 'horizontal', 'centered'] as const;
export type ModalHeaderLayout = (typeof MODAL_HEADER_LAYOUTS)[number];

export interface ModalHeaderProps {
  icon?: ReactNode;
  showPattern?: boolean;
  title?: string;
  subtitle?: string;
  layout?: ModalHeaderLayout;
  /**
   * Defaults to 2, which is appropriate for modal dialogs to avoid conflicting with page h1.
   */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
  /**
   * Renders a full-width bottom divider, matching the Untitled modal header that
   * sits above a bordered body.
   */
  divider?: boolean;
  /**
   * Overrides title/subtitle if provided.
   */
  children?: ReactNode;
}

const IconContainer = ({ icon, showPattern }: { icon: ReactNode; showPattern: boolean }) => (
  <div className="relative flex size-12 items-center justify-center rounded-2xl border border-primary bg-primary shadow-xs">
    {showPattern && <CirclePattern />}
    <div className="relative z-10">{icon}</div>
  </div>
);

const TextContent = ({
  title,
  subtitle,
  level,
  className,
}: {
  title?: string;
  subtitle?: string;
  level: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
}) => {
  if (!title && !subtitle) return null;

  return (
    <div className={cx('relative', className)}>
      {title && (
        <Heading slot="title" level={level} className="text-md font-semibold text-primary">
          {title}
        </Heading>
      )}
      {subtitle && <p className="text-sm text-tertiary">{subtitle}</p>}
    </div>
  );
};

export const ModalHeader = ({
  icon,
  showPattern = false,
  title,
  subtitle,
  layout = 'vertical',
  level = 2,
  className,
  divider = false,
  children,
}: ModalHeaderProps) => {
  const dividerClassName = divider ? 'border-b border-secondary' : '';

  if (children) {
    return <div className={cx('px-6 py-6', dividerClassName, className)}>{children}</div>;
  }

  const baseClassName = cx('px-6 py-6 overflow-hidden', dividerClassName);

  if (layout === 'centered') {
    return (
      <div className={cx(baseClassName, 'text-center', className)}>
        {icon && (
          <div className="mb-4 flex justify-center">
            <IconContainer icon={icon} showPattern={showPattern} />
          </div>
        )}
        <TextContent title={title} subtitle={subtitle} level={level} />
      </div>
    );
  } else if (layout === 'horizontal') {
    return (
      <div className={cx(baseClassName, className)}>
        <div className="flex items-start gap-4">
          {icon && (
            <div className="shrink-0">
              <IconContainer icon={icon} showPattern={showPattern} />
            </div>
          )}
          <TextContent title={title} subtitle={subtitle} level={level} className="flex-1 pr-8" />
        </div>
      </div>
    );
  } else {
    return (
      <div className={cx(baseClassName, className)}>
        {icon && (
          <div className="mb-3">
            <IconContainer icon={icon} showPattern={showPattern} />
          </div>
        )}
        <TextContent title={title} subtitle={subtitle} level={level} className="pr-8" />
      </div>
    );
  }
};
